import { CloseSharp, CloudUpload } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { Box } from '@mui/system';

import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { Accept, DropzoneProps, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FormikErrors } from 'formik/dist/types';
import { translations } from 'locales/i18n';

const getColor = (props: any) => {
  if (props.isDragAccept) {
    return '#00e676';
  }
  if (props.isDragReject || (props.errors && props.errors[props.field])) {
    return '#ff1744';
  }
  if (props.isDragActive) {
    return '#2196f3';
  }
  return '#eeeeee';
};

export const DropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 0.5em;
  align-items: center;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${props => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
  &:hover {
    background-color: #eee;
  }
`;

export type FieldValueFunction = (field: string, value: any, shouldValidate?: boolean) => void;

interface FormDropzoneProps {
  name: string;
  errors?: FormikErrors<any>;
  value: any;
  setFieldValue: FieldValueFunction;
  accept?: Accept;
  placeholder?: string;
  className?: string;
  showPreview?: boolean;
  single?: boolean;
  setPaths?: (paths: string[]) => void;
  size?: 'small' | 'medium';
}

const StyledDropzone = (props: FormDropzoneProps & DropzoneProps) => {
  const [paths, setPaths] = useState(['']);
  const { t } = useTranslation();
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      props.setFieldValue(
        props.name,
        props.multiple
          ? [
              ...props.value.filter(
                existing => acceptedFiles.findIndex(newVal => newVal.name === existing.name) === -1
              ),
              ...acceptedFiles
            ]
          : acceptedFiles[0]
      );
      setPaths(acceptedFiles.map(file => URL.createObjectURL(file)));
      props.setPaths && props.setPaths(acceptedFiles.map(file => URL.createObjectURL(file)));
    },
    [props]
  );
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, fileRejections } =
    useDropzone({
      onDrop,
      ...(props.accept ? { accept: props.accept } : {}),
      ...(props.single && { maxFiles: 1 })
    });

  return (
    <div className={clsx('container', props.className)}>
      <DropzoneContainer
        {...(getRootProps({ isDragActive, isDragAccept, isDragReject }) as any)}
        errors={props.errors}
        field={props.name}
      >
        <input {...getInputProps()} />
        <span
          style={{
            alignItems: 'center',
            maxWidth: '100%'
          }}
        >
          {!props.multiple && props?.value?.name ? (
            <FileControl fileName={props?.value?.originalname ?? props?.value?.name} />
          ) : (
            <p style={props.size === 'small' ? { margin: 6 } : {}}>
              <CloudUpload style={{ marginRight: '0.5em' }} />
              {props.placeholder || 'File Upload'}
            </p>
          )}
        </span>
      </DropzoneContainer>
      {!!fileRejections.length && (
        <Alert severity="error">{t(translations.common.tooManyImages())}</Alert>
      )}
      {props.showPreview &&
        !!paths[0]?.length &&
        paths.map(path => <img key={path} src={path} alt="preview" />)}
      {Array.isArray(props?.value) &&
        props?.value?.length > 0 &&
        props.multiple &&
        props.value.map(f => (
          <FileControl
            key={f.id || f.path}
            fileName={f.originalname ?? f.name}
            onDelete={() =>
              props.setFieldValue(
                props.name,
                props.value.filter(file => f.name !== file.name)
              )
            }
          />
        ))}
    </div>
  );
};

const FileControl = ({
  fileName,
  onDelete
}: {
  fileName: string;
  onDelete?: (fileName: string) => void;
}) => {
  return (
    <Box mt={1} mr={1} mb={1}>
      <p>{fileName}</p>
      {onDelete && (
        <CloseSharp
          style={{
            fontSize: 20,
            color: 'red',
            cursor: 'pointer'
          }}
          onClick={() => onDelete(fileName)}
        />
      )}
    </Box>
  );
};

export default StyledDropzone;
